import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { ListOrdered, Plus, Trash2, Loader2, Settings, CornerDownRight, Eye, GitBranch, BarChart3, ArrowUp, ArrowDown, UserCheck, HelpCircle } from "lucide-react";

const OPCION_VACIA = { etiqueta: "", tipo: "departamento", department_id: "", department_name: "", assigned_user_id: "", assigned_user_name: "", disponible_24_7: true, mensaje_fuera_horario: "", sub_mensaje: "", faq_mensaje: "", preguntas: [], sub_opciones: [] };
const SUB_VACIA = { etiqueta: "", tipo: "departamento", department_id: "", department_name: "", assigned_user_id: "", assigned_user_name: "", disponible_24_7: true, mensaje_fuera_horario: "", sub_mensaje: "", faq_mensaje: "", preguntas: [], sub_opciones: [] };
const SUBSUB_VACIA = { etiqueta: "", department_id: "", department_name: "", assigned_user_id: "", assigned_user_name: "", disponible_24_7: true, mensaje_fuera_horario: "", preguntas: [] };

const FORM_DEFAULT = {
  config_id: "",
  is_active: true,
  mensaje_bienvenida: "¡Hola! 👋 Gracias por comunicarse con nosotros. ¿En qué podemos ayudarle?",
  opciones: [],
  max_reintentos: 3,
  reactivar_horas: 24,
  alerta_sin_atencion_minutos: 0,
  mensaje_invalido: "⚠️ Opción no válida.",
  mensaje_confirmacion: "Perfecto ✅ Le comunicamos con {departamento}. En breve un agente le atenderá.",
  mensaje_fallback: "Le comunicamos con un agente que le atenderá en breve. Gracias por su paciencia. 🙏"
};

const numEmoji = (n) => (n >= 1 && n <= 9 ? `${n}\uFE0F\u20E3` : `${n}.`);

const buildPreview = (form) => {
  const lineas = form.mensaje_bienvenida ? [form.mensaje_bienvenida, ""] : [];
  form.opciones.forEach((op, i) => {
    lineas.push(`${numEmoji(i + 1)} ${op.etiqueta || "(sin etiqueta)"}`);
    if (op.tipo === "submenu") {
      (op.sub_opciones || []).forEach((so, j) => {
        lineas.push(`      ↳ ${numEmoji(j + 1)} ${so.etiqueta || "(sin etiqueta)"}${so.tipo === "faq" ? " ❓" : ""}`);
        if (so.tipo === "submenu") {
          (so.sub_opciones || []).forEach((sso, k) => lineas.push(`            ↳ ${numEmoji(k + 1)} ${sso.etiqueta || "(sin etiqueta)"}`));
        }
      });
      lineas.push(`      ↳ 0\uFE0F\u20E3 Volver al inicio`);
    }
  });
  lineas.push("", "Responda con el *número* de la opción deseada.");
  return lineas.join("\n");
};

export const WhatsAppIVRSection = ({ configs, departamentos, isAdmin, getAuthHeaders }) => {
  const [ivrConfigs, setIvrConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(FORM_DEFAULT);
  const [statsConfig, setStatsConfig] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [statsDays, setStatsDays] = useState("30");
  const [loadingStats, setLoadingStats] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [faqPendientes, setFaqPendientes] = useState([]);
  const [faqEdits, setFaqEdits] = useState({});
  const [faqAccionId, setFaqAccionId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/users`, getAuthHeaders())
      .then((res) => setUsuarios((res.data || []).filter((u) => u.is_active !== false)))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchIvr = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/whatsapp/ivr/config`, getAuthHeaders());
      setIvrConfigs(res.data || []);
    } catch (err) {
      toast.error("Error al cargar configuraciones IVR");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchIvr(); }, [fetchIvr]);

  const deptName = (id) => {
    const d = departamentos.find((x) => x.id === id);
    return d ? (d.name || d.nombre) : "";
  };

  const usuariosOrdenados = (depId) => {
    const nombreDep = (deptName(depId) || "").toLowerCase();
    return [...usuarios].sort((a, b) => {
      const am = (a.department || "").toLowerCase() === nombreDep && nombreDep ? 0 : 1;
      const bm = (b.department || "").toLowerCase() === nombreDep && nombreDep ? 0 : 1;
      return am - bm || (a.name || a.email || "").localeCompare(b.name || b.email || "");
    });
  };

  const userName = (u) => u.name || u.email;

  const moverOpcion = (idx, dir) => {
    setForm((f) => {
      const nuevo = [...f.opciones];
      const j = idx + dir;
      if (j < 0 || j >= nuevo.length) return f;
      [nuevo[idx], nuevo[j]] = [nuevo[j], nuevo[idx]];
      return { ...f, opciones: nuevo };
    });
  };

  const agregarMenu = () => {
    setForm((f) => ({ ...f, opciones: [...f.opciones, { ...OPCION_VACIA }] }));
  };

  const agregarSubmenuA = (idx) => {
    setForm((f) => ({
      ...f,
      opciones: f.opciones.map((o, i) =>
        i === idx
          ? { ...o, tipo: "submenu", sub_opciones: [...(o.sub_opciones || []), { ...SUB_VACIA }] }
          : o
      )
    }));
  };

  const openEditor = (config) => {
    const existente = ivrConfigs.find((i) => i.config_id === config.id);
    setForm(existente ? { ...FORM_DEFAULT, ...existente } : { ...FORM_DEFAULT, config_id: config.id });
    setShowDialog(true);
  };

  const fetchStats = async (configId, days) => {
    setLoadingStats(true);
    try {
      const res = await axios.get(`${API}/whatsapp/ivr/stats?config_id=${configId}&days=${days}`, getAuthHeaders());
      setStatsData(res.data);
    } catch (err) {
      toast.error("Error al cargar estadísticas");
    } finally {
      setLoadingStats(false);
    }
  };

  const openStats = (config) => {
    setStatsConfig(config);
    setStatsData(null);
    setFaqPendientes([]);
    setFaqEdits({});
    fetchStats(config.id, statsDays);
    axios.get(`${API}/whatsapp/faq/sin-respuesta?config_id=${config.id}`, getAuthHeaders())
      .then((res) => setFaqPendientes(res.data || []))
      .catch(() => setFaqPendientes([]));
  };

  const convertirFaq = async (item) => {
    const edit = faqEdits[item.id] || {};
    const respuesta = (edit.respuesta || "").trim();
    if (!respuesta) {
      toast.error("Escriba la respuesta del FAQ");
      return;
    }
    setFaqAccionId(item.id);
    try {
      await axios.post(`${API}/whatsapp/faq/sin-respuesta/${item.id}/convertir`, {
        pregunta: (edit.pregunta ?? item.pregunta),
        respuesta
      }, getAuthHeaders());
      toast.success("Agregada a los FAQs del Chatbot IA");
      setFaqPendientes((l) => l.filter((x) => x.id !== item.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al convertir en FAQ");
    } finally {
      setFaqAccionId(null);
    }
  };

  const descartarFaq = async (item) => {
    setFaqAccionId(item.id);
    try {
      await axios.delete(`${API}/whatsapp/faq/sin-respuesta/${item.id}`, getAuthHeaders());
      setFaqPendientes((l) => l.filter((x) => x.id !== item.id));
    } catch (err) {
      toast.error("Error al descartar la pregunta");
    } finally {
      setFaqAccionId(null);
    }
  };

  const setOpcion = (idx, cambios) => {
    setForm((f) => ({ ...f, opciones: f.opciones.map((o, i) => (i === idx ? { ...o, ...cambios } : o)) }));
  };

  const setSubOpcion = (idx, sidx, cambios) => {
    setForm((f) => ({
      ...f,
      opciones: f.opciones.map((o, i) =>
        i === idx ? { ...o, sub_opciones: o.sub_opciones.map((s, j) => (j === sidx ? { ...s, ...cambios } : s)) } : o
      )
    }));
  };

  const handleGuardar = async () => {
    if (form.opciones.length === 0) {
      toast.error("Agregue al menos una opción al menú");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/whatsapp/ivr/config`, {
        ...form,
        max_reintentos: parseInt(form.max_reintentos, 10) || 3,
        reactivar_horas: parseInt(form.reactivar_horas, 10) || 24,
        alerta_sin_atencion_minutos: parseInt(form.alerta_sin_atencion_minutos, 10) || 0
      }, getAuthHeaders());
      toast.success("Menú IVR guardado");
      setShowDialog(false);
      fetchIvr();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar el IVR");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (ivr, activo) => {
    try {
      await axios.post(`${API}/whatsapp/ivr/config`, { ...ivr, is_active: activo }, getAuthHeaders());
      toast.success(activo ? "IVR activado" : "IVR desactivado");
      fetchIvr();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar");
    }
  };

  const handleEliminar = async (ivr) => {
    if (!window.confirm("¿Eliminar el menú IVR de esta línea?")) return;
    try {
      await axios.delete(`${API}/whatsapp/ivr/config/${ivr.config_id}`, getAuthHeaders());
      toast.success("Menú IVR eliminado");
      fetchIvr();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="ivr-section">
      <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20">
        <CardContent className="pt-5 pb-5 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center shrink-0">
            <ListOrdered className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <p className="font-semibold text-emerald-900 dark:text-emerald-100">Menú de bienvenida (IVR)</p>
            <p className="text-sm text-emerald-700 dark:text-emerald-300">
              Al iniciar una conversación (o tras un período de inactividad), el bot envía un menú numerado y redirige la conversación
              al departamento que el cliente elija. Soporta submenús y, si el cliente no elige una opción válida, se pasa a un agente.
            </p>
          </div>
        </CardContent>
      </Card>

      {configs.length === 0 ? (
        <Card className="border-dashed border-2"><CardContent className="py-10 text-center text-slate-500">Primero configura una línea de WhatsApp</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {configs.map((config) => {
            const ivr = ivrConfigs.find((i) => i.config_id === config.id);
            return (
              <Card key={config.id} data-testid={`ivr-card-${config.id}`} className={ivr?.is_active ? "border-emerald-200" : ""}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <ListOrdered className="w-4 h-4 text-emerald-600" />
                      {config.name || config.phone_number}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {ivr && (
                        <Badge variant={ivr.is_active ? "default" : "secondary"} className={ivr.is_active ? "bg-emerald-500" : ""} data-testid={`ivr-estado-${config.id}`}>
                          {ivr.is_active ? "Activo" : "Inactivo"}
                        </Badge>
                      )}
                      {ivr && isAdmin && (
                        <Switch checked={!!ivr.is_active} onCheckedChange={(v) => handleToggle(ivr, v)} data-testid={`ivr-toggle-${config.id}`} />
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {ivr ? (
                    <div className="space-y-1">
                      {ivr.opciones.map((op) => (
                        <div key={op.numero} className="text-sm text-slate-600 dark:text-slate-300 flex items-center gap-2">
                          <span>{numEmoji(parseInt(op.numero, 10))}</span>
                          <span>{op.etiqueta}</span>
                          {op.tipo === "submenu" ? (
                            <Badge variant="outline" className="text-xs"><GitBranch className="w-3 h-3 mr-1" />{(op.sub_opciones || []).length} sub-opciones</Badge>
                          ) : op.tipo === "faq" ? (
                            <Badge variant="outline" className="text-xs text-amber-600 border-amber-300"><HelpCircle className="w-3 h-3 mr-1" />FAQs</Badge>
                          ) : (
                            <span className="text-xs text-slate-400">→ {op.department_name || deptName(op.department_id)}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">Sin menú IVR configurado para esta línea.</p>
                  )}
                  {isAdmin && (
                    <div className="flex gap-2 pt-2 border-t">
                      <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditor(config)} data-testid={`ivr-configurar-${config.id}`}>
                        <Settings className="w-4 h-4 mr-2" />{ivr ? "Editar menú" : "Crear menú"}
                      </Button>
                      {ivr && (
                        <Button variant="outline" size="sm" onClick={() => openStats(config)} data-testid={`ivr-stats-${config.id}`}>
                          <BarChart3 className="w-4 h-4 mr-1" />Estadísticas
                        </Button>
                      )}
                      {ivr && (
                        <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleEliminar(ivr)} data-testid={`ivr-eliminar-${config.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!statsConfig} onOpenChange={(v) => !v && setStatsConfig(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-emerald-600" />
              Estadísticas del IVR — {statsConfig?.name || statsConfig?.phone_number}
            </DialogTitle>
            <DialogDescription>Cuántos clientes eligen cada opción y qué departamentos reciben más conversaciones.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <Label className="text-sm">Período:</Label>
            <Select value={statsDays} onValueChange={(v) => { setStatsDays(v); if (statsConfig) fetchStats(statsConfig.id, v); }}>
              <SelectTrigger className="w-44" data-testid="ivr-stats-periodo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Últimos 7 días</SelectItem>
                <SelectItem value="30">Últimos 30 días</SelectItem>
                <SelectItem value="90">Últimos 90 días</SelectItem>
                <SelectItem value="0">Todo el historial</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {loadingStats ? (
            <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
          ) : statsData && (
            <div className="space-y-5" data-testid="ivr-stats-contenido">
              <div className="grid grid-cols-3 gap-3">
                <Card><CardContent className="pt-4 pb-4 text-center">
                  <p className="text-2xl font-bold text-emerald-600" data-testid="ivr-stats-total">{statsData.total_selecciones}</p>
                  <p className="text-xs text-slate-500">Selecciones</p>
                </CardContent></Card>
                <Card><CardContent className="pt-4 pb-4 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{statsData.total_ruteos}</p>
                  <p className="text-xs text-slate-500">Enrutadas a departamento</p>
                </CardContent></Card>
                <Card><CardContent className="pt-4 pb-4 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{statsData.clientes_unicos}</p>
                  <p className="text-xs text-slate-500">Clientes únicos</p>
                </CardContent></Card>
              </div>

              {(statsData.faq && (statsData.faq.respondidas > 0 || statsData.faq.a_agente > 0)) && (
                <div data-testid="ivr-stats-faq">
                  <p className="font-semibold text-sm mb-2 text-slate-700 dark:text-slate-200">❓ Preguntas frecuentes (FAQs)</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Card><CardContent className="pt-4 pb-4 text-center">
                      <p className="text-2xl font-bold text-emerald-600" data-testid="ivr-stats-faq-respondidas">{statsData.faq.respondidas}</p>
                      <p className="text-xs text-slate-500">Preguntas respondidas por el bot</p>
                    </CardContent></Card>
                    <Card><CardContent className="pt-4 pb-4 text-center">
                      <p className="text-2xl font-bold text-amber-600" data-testid="ivr-stats-faq-agente">{statsData.faq.a_agente}</p>
                      <p className="text-xs text-slate-500">Pasaron a un agente</p>
                      <p className="text-[11px] text-slate-400 mt-1">
                        {statsData.faq.a_agente_sin_respuesta} sin respuesta · {statsData.faq.a_agente_pedido} lo pidió el cliente
                      </p>
                    </CardContent></Card>
                  </div>
                </div>
              )}

              {faqPendientes.length > 0 && (
                <div data-testid="faq-pendientes-seccion">
                  <p className="font-semibold text-sm mb-1 text-slate-700 dark:text-slate-200">📝 Preguntas sin respuesta ({faqPendientes.length})</p>
                  <p className="text-xs text-slate-500 mb-2">El bot no supo responder estas preguntas. Escriba la respuesta y conviértalas en FAQs del Chatbot IA para que las responda la próxima vez.</p>
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                    {faqPendientes.map((item) => (
                      <div key={item.id} className="border rounded-md p-2 space-y-2 bg-slate-50 dark:bg-slate-800/40" data-testid={`faq-pendiente-${item.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-slate-400">
                            {new Date(item.timestamp).toLocaleDateString()} · {item.contact_phone || "cliente"}{(item.veces || 1) > 1 ? ` · preguntada ${item.veces} veces` : ""}
                          </span>
                          <Button variant="ghost" size="sm" className="text-red-500 h-7 px-2" disabled={faqAccionId === item.id} onClick={() => descartarFaq(item)} title="Descartar" data-testid={`faq-descartar-${item.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <Input
                          value={faqEdits[item.id]?.pregunta ?? item.pregunta}
                          onChange={(e) => setFaqEdits((s) => ({ ...s, [item.id]: { ...s[item.id], pregunta: e.target.value } }))}
                          data-testid={`faq-pregunta-${item.id}`} />
                        <div className="flex gap-2">
                          <Input
                            placeholder="Escriba la respuesta para este FAQ..."
                            value={faqEdits[item.id]?.respuesta || ""}
                            onChange={(e) => setFaqEdits((s) => ({ ...s, [item.id]: { ...s[item.id], respuesta: e.target.value } }))}
                            data-testid={`faq-respuesta-${item.id}`} />
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 shrink-0" disabled={faqAccionId === item.id || !(faqEdits[item.id]?.respuesta || "").trim()} onClick={() => convertirFaq(item)} data-testid={`faq-convertir-${item.id}`}>
                            {faqAccionId === item.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                            Convertir en FAQ
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="font-semibold text-sm mb-2 text-slate-700 dark:text-slate-200">🏢 Departamentos que más reciben</p>
                {statsData.departamentos.length === 0 ? (
                  <p className="text-sm text-slate-500">Aún no hay conversaciones enrutadas en este período.</p>
                ) : statsData.departamentos.map((d) => (
                  <div key={d.departamento} className="mb-2" data-testid={`ivr-stats-depto-${d.departamento}`}>
                    <div className="flex justify-between text-sm mb-1">
                      <span>{d.departamento}</span>
                      <span className="text-slate-500">{d.conteo} ({d.porcentaje}%)</span>
                    </div>
                    <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${d.porcentaje}%` }} />
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <p className="font-semibold text-sm mb-2 text-slate-700 dark:text-slate-200">🔢 Selecciones por opción del menú</p>
                {statsData.opciones.length === 0 ? (
                  <p className="text-sm text-slate-500">Sin selecciones registradas en este período.</p>
                ) : statsData.opciones.map((o) => {
                  const max = statsData.opciones[0]?.conteo || 1;
                  return (
                    <div key={o.etiqueta} className="mb-2" data-testid={`ivr-stats-opcion-${o.etiqueta}`}>
                      <div className="flex justify-between text-sm mb-1">
                        <span>{o.etiqueta}</span>
                        <span className="text-slate-500">{o.conteo}</span>
                      </div>
                      <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                        <div className="h-full bg-teal-400 rounded-full" style={{ width: `${(o.conteo * 100) / max}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListOrdered className="w-5 h-5 text-emerald-600" />
              Menú IVR de bienvenida
            </DialogTitle>
            <DialogDescription>El cliente elige una opción con el número y el bot redirige la conversación.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Mensaje de bienvenida</Label>
              <Textarea value={form.mensaje_bienvenida} onChange={(e) => setForm({ ...form, mensaje_bienvenida: e.target.value })} rows={2} data-testid="ivr-mensaje-bienvenida" />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Opciones del menú</Label>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="ivr-agregar-opcion">
                      <Plus className="w-4 h-4 mr-1" />Agregar opción
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64">
                    <DropdownMenuItem onClick={agregarMenu} data-testid="ivr-agregar-menu" className="cursor-pointer">
                      <ListOrdered className="w-4 h-4 mr-2 text-emerald-600" />Menú principal
                    </DropdownMenuItem>
                    {form.opciones.length > 0 && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="text-xs text-slate-500">Submenú dentro de:</DropdownMenuLabel>
                        {form.opciones.map((o, i) => (
                          <DropdownMenuItem key={i} onClick={() => agregarSubmenuA(i)} data-testid={`ivr-agregar-submenu-${i}`} className="cursor-pointer">
                            <GitBranch className="w-4 h-4 mr-2 text-teal-600" />
                            {numEmoji(i + 1)} {o.etiqueta || "(sin etiqueta)"}
                          </DropdownMenuItem>
                        ))}
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              {form.opciones.length === 0 && <p className="text-sm text-slate-500">Agregue las opciones que verá el cliente (ej. Renovaciones, Reclamos...).</p>}
              {form.opciones.map((op, idx) => (
                <div key={idx} className="border rounded-lg p-3 space-y-2 bg-slate-50 dark:bg-slate-800/50" data-testid={`ivr-opcion-${idx}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-lg shrink-0">{numEmoji(idx + 1)}</span>
                    <Input placeholder="Etiqueta (ej. Renovaciones)" value={op.etiqueta} onChange={(e) => setOpcion(idx, { etiqueta: e.target.value })} data-testid={`ivr-opcion-etiqueta-${idx}`} />
                    <Select value={op.tipo} onValueChange={(v) => setOpcion(idx, { tipo: v })}>
                      <SelectTrigger className="w-40 shrink-0" data-testid={`ivr-opcion-tipo-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="departamento">Departamento</SelectItem>
                        <SelectItem value="submenu">Submenú</SelectItem>
                        <SelectItem value="faq">FAQs / Preguntas</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="sm" className="shrink-0 px-2" disabled={idx === 0} onClick={() => moverOpcion(idx, -1)} data-testid={`ivr-opcion-subir-${idx}`} title="Subir">
                      <ArrowUp className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="shrink-0 px-2" disabled={idx === form.opciones.length - 1} onClick={() => moverOpcion(idx, 1)} data-testid={`ivr-opcion-bajar-${idx}`} title="Bajar">
                      <ArrowDown className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-500 shrink-0" onClick={() => setForm({ ...form, opciones: form.opciones.filter((_, i) => i !== idx) })} data-testid={`ivr-opcion-quitar-${idx}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>

                  {op.tipo === "faq" ? (
                    <div className="space-y-2">
                      <Input placeholder="Mensaje al entrar a las FAQs (opcional)" value={op.faq_mensaje || ""} onChange={(e) => setOpcion(idx, { faq_mensaje: e.target.value })} data-testid={`ivr-opcion-faq-mensaje-${idx}`} />
                      <p className="text-xs text-slate-500 flex items-center gap-1">
                        <HelpCircle className="w-3 h-3 shrink-0" />
                        Responde con las FAQs del Chatbot IA de esta línea (misma base de datos: al modificarlas allá, se actualizan aquí). Si no hay respuesta, se avisa al cliente y se transfiere a un agente.
                      </p>
                    </div>
                  ) : op.tipo === "departamento" ? (
                    <div className="space-y-2">
                      <Select value={op.department_id || ""} onValueChange={(v) => setOpcion(idx, { department_id: v, department_name: deptName(v), assigned_user_id: "", assigned_user_name: "" })}>
                        <SelectTrigger data-testid={`ivr-opcion-depto-${idx}`}><SelectValue placeholder="Redirigir al departamento..." /></SelectTrigger>
                        <SelectContent>
                          {departamentos.map((d) => <SelectItem key={d.id} value={d.id}>{d.name || d.nombre}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {op.department_id && (
                        <div className="flex items-center gap-2">
                          <UserCheck className="w-4 h-4 text-slate-400 shrink-0" />
                          <Select value={op.assigned_user_id || "default"} onValueChange={(v) => {
                            if (v === "default") { setOpcion(idx, { assigned_user_id: "", assigned_user_name: "" }); }
                            else { const u = usuarios.find((x) => x.id === v); setOpcion(idx, { assigned_user_id: v, assigned_user_name: u ? userName(u) : "" }); }
                          }}>
                            <SelectTrigger data-testid={`ivr-opcion-ejecutivo-${idx}`}><SelectValue placeholder="Ejecutivo..." /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="default">Ejecutivo por defecto del departamento</SelectItem>
                              {usuariosOrdenados(op.department_id).map((u) => <SelectItem key={u.id} value={u.id}>{userName(u)}{(u.department || "").toLowerCase() === (deptName(op.department_id) || "").toLowerCase() && u.department ? " ★" : ""}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Switch checked={op.disponible_24_7 !== false} onCheckedChange={(v) => setOpcion(idx, { disponible_24_7: v })} data-testid={`ivr-opcion-247-${idx}`} />
                        <span className="text-sm text-slate-600 dark:text-slate-300">Disponible 24/7</span>
                      </div>
                      {op.disponible_24_7 === false && (
                        <Input placeholder="Mensaje si el cliente escribe fuera de horario..." value={op.mensaje_fuera_horario || ""} onChange={(e) => setOpcion(idx, { mensaje_fuera_horario: e.target.value })} data-testid={`ivr-opcion-fuera-horario-${idx}`} />
                      )}
                      <div className="space-y-1 pt-1 border-t border-dashed">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-slate-500">📝 Preguntas de recolección (opcional) — el bot las hará antes de pasar al ejecutivo</span>
                          <Button variant="ghost" size="sm" className="h-6 px-2 text-emerald-700" onClick={() => setOpcion(idx, { preguntas: [...(op.preguntas || []), ""] })} data-testid={`ivr-opcion-agregar-pregunta-${idx}`}>
                            <Plus className="w-3 h-3 mr-1" />Pregunta
                          </Button>
                        </div>
                        {(op.preguntas || []).map((pq, pi) => (
                          <div key={pi} className="flex items-center gap-1">
                            <span className="text-xs text-slate-400 shrink-0 w-4">{pi + 1}.</span>
                            <Input className="h-8 text-sm" placeholder="Ej: ¿Cuál es el número de póliza que desea pagar?" value={pq}
                              onChange={(e) => setOpcion(idx, { preguntas: op.preguntas.map((x, j) => (j === pi ? e.target.value : x)) })}
                              data-testid={`ivr-opcion-pregunta-${idx}-${pi}`} />
                            <Button variant="ghost" size="sm" className="text-red-500 h-7 w-7 p-0 shrink-0" onClick={() => setOpcion(idx, { preguntas: op.preguntas.filter((_, j) => j !== pi) })} data-testid={`ivr-opcion-quitar-pregunta-${idx}-${pi}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="ml-6 space-y-2">
                      <Input placeholder="Mensaje del submenú (opcional)" value={op.sub_mensaje || ""} onChange={(e) => setOpcion(idx, { sub_mensaje: e.target.value })} data-testid={`ivr-submenu-mensaje-${idx}`} />
                      {(op.sub_opciones || []).map((so, sidx) => (
                        <div key={sidx} className="space-y-1">
                          <div className="flex items-center gap-2">
                            <CornerDownRight className="w-4 h-4 text-slate-400 shrink-0" />
                            <span className="text-sm shrink-0">{numEmoji(sidx + 1)}</span>
                            <Input placeholder="Etiqueta (ej. Auto)" value={so.etiqueta} onChange={(e) => setSubOpcion(idx, sidx, { etiqueta: e.target.value })} data-testid={`ivr-sub-etiqueta-${idx}-${sidx}`} />
                            <Select value={so.tipo || "departamento"} onValueChange={(v) => setSubOpcion(idx, sidx, { tipo: v, ...(v === "submenu" && !(so.sub_opciones || []).length ? { sub_opciones: [{ ...SUBSUB_VACIA }] } : {}) })}>
                              <SelectTrigger className="w-36 shrink-0" data-testid={`ivr-sub-tipo-${idx}-${sidx}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="departamento">Departamento</SelectItem>
                                <SelectItem value="submenu">Submenú</SelectItem>
                                <SelectItem value="faq">FAQs</SelectItem>
                              </SelectContent>
                            </Select>
                            {so.tipo !== "faq" && so.tipo !== "submenu" && (
                              <Select value={so.department_id || ""} onValueChange={(v) => setSubOpcion(idx, sidx, { department_id: v, department_name: deptName(v), assigned_user_id: "", assigned_user_name: "" })}>
                                <SelectTrigger className="w-44 shrink-0" data-testid={`ivr-sub-depto-${idx}-${sidx}`}><SelectValue placeholder="Departamento..." /></SelectTrigger>
                                <SelectContent>
                                  {departamentos.map((d) => <SelectItem key={d.id} value={d.id}>{d.name || d.nombre}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            )}
                            <Button variant="ghost" size="sm" className="text-red-500 shrink-0" onClick={() => setOpcion(idx, { sub_opciones: op.sub_opciones.filter((_, j) => j !== sidx) })} data-testid={`ivr-sub-quitar-${idx}-${sidx}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          {so.tipo === "submenu" && (
                            <div className="pl-12 space-y-2 border-l-2 border-indigo-200 ml-6">
                              <Input placeholder="Mensaje del sub-submenú (opcional)" value={so.sub_mensaje || ""} onChange={(e) => setSubOpcion(idx, sidx, { sub_mensaje: e.target.value })} data-testid={`ivr-subsub-mensaje-${idx}-${sidx}`} />
                              {(so.sub_opciones || []).map((sso, ssidx) => (
                                <div key={ssidx} className="flex items-center gap-2 flex-wrap">
                                  <CornerDownRight className="w-4 h-4 text-indigo-400 shrink-0" />
                                  <span className="text-sm shrink-0">{numEmoji(ssidx + 1)}</span>
                                  <Input className="flex-1 min-w-[140px]" placeholder="Etiqueta (ej. nombre del agente)" value={sso.etiqueta}
                                    onChange={(e) => setSubOpcion(idx, sidx, { sub_opciones: so.sub_opciones.map((x, j) => (j === ssidx ? { ...x, etiqueta: e.target.value } : x)) })}
                                    data-testid={`ivr-subsub-etiqueta-${idx}-${sidx}-${ssidx}`} />
                                  <Select value={sso.department_id || ""} onValueChange={(v) => setSubOpcion(idx, sidx, { sub_opciones: so.sub_opciones.map((x, j) => (j === ssidx ? { ...x, department_id: v, department_name: deptName(v), assigned_user_id: "", assigned_user_name: "" } : x)) })}>
                                    <SelectTrigger className="w-40 shrink-0" data-testid={`ivr-subsub-depto-${idx}-${sidx}-${ssidx}`}><SelectValue placeholder="Departamento..." /></SelectTrigger>
                                    <SelectContent>
                                      {departamentos.map((d) => <SelectItem key={d.id} value={d.id}>{d.name || d.nombre}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                  {sso.department_id && (
                                    <Select value={sso.assigned_user_id || "default"} onValueChange={(v) => {
                                      const u = v === "default" ? null : usuarios.find((x) => x.id === v);
                                      setSubOpcion(idx, sidx, { sub_opciones: so.sub_opciones.map((x, j) => (j === ssidx ? { ...x, assigned_user_id: u ? v : "", assigned_user_name: u ? userName(u) : "" } : x)) });
                                    }}>
                                      <SelectTrigger className="w-44 shrink-0" data-testid={`ivr-subsub-ejecutivo-${idx}-${sidx}-${ssidx}`}><SelectValue placeholder="Agente..." /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="default">Ejecutivo por defecto del departamento</SelectItem>
                                        {usuariosOrdenados(sso.department_id).map((u) => <SelectItem key={u.id} value={u.id}>{userName(u)}</SelectItem>)}
                                      </SelectContent>
                                    </Select>
                                  )}
                                  <Button variant="ghost" size="sm" className="text-red-500 shrink-0" onClick={() => setSubOpcion(idx, sidx, { sub_opciones: so.sub_opciones.filter((_, j) => j !== ssidx) })} data-testid={`ivr-subsub-quitar-${idx}-${sidx}-${ssidx}`}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              ))}
                              <Button variant="outline" size="sm" onClick={() => setSubOpcion(idx, sidx, { sub_opciones: [...(so.sub_opciones || []), { ...SUBSUB_VACIA }] })} data-testid={`ivr-agregar-subsub-${idx}-${sidx}`}>
                                <Plus className="w-4 h-4 mr-1" />Agregar opción del sub-submenú
                              </Button>
                            </div>
                          )}
                          {so.tipo === "faq" && (
                            <div className="pl-12 space-y-1">
                              <Input placeholder="Mensaje al entrar a las FAQs (opcional)" value={so.faq_mensaje || ""} onChange={(e) => setSubOpcion(idx, sidx, { faq_mensaje: e.target.value })} data-testid={`ivr-sub-faq-mensaje-${idx}-${sidx}`} />
                              <p className="text-xs text-slate-500">Usa las FAQs del Chatbot IA de esta línea (misma base de datos).</p>
                            </div>
                          )}
                          {so.tipo !== "faq" && so.department_id && (
                            <div className="flex items-center gap-2 pl-12">
                              <UserCheck className="w-4 h-4 text-slate-400 shrink-0" />
                              <Select value={so.assigned_user_id || "default"} onValueChange={(v) => {
                                if (v === "default") { setSubOpcion(idx, sidx, { assigned_user_id: "", assigned_user_name: "" }); }
                                else { const u = usuarios.find((x) => x.id === v); setSubOpcion(idx, sidx, { assigned_user_id: v, assigned_user_name: u ? userName(u) : "" }); }
                              }}>
                                <SelectTrigger className="flex-1" data-testid={`ivr-sub-ejecutivo-${idx}-${sidx}`}><SelectValue placeholder="Ejecutivo..." /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="default">Ejecutivo por defecto del departamento</SelectItem>
                                  {usuariosOrdenados(so.department_id).map((u) => <SelectItem key={u.id} value={u.id}>{userName(u)}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                          {so.tipo !== "faq" && so.tipo !== "submenu" && (
                          <div className="flex items-center gap-2 pl-12">
                            <Switch checked={so.disponible_24_7 !== false} onCheckedChange={(v) => setSubOpcion(idx, sidx, { disponible_24_7: v })} data-testid={`ivr-sub-247-${idx}-${sidx}`} />
                            <span className="text-xs text-slate-500">24/7</span>
                            {so.disponible_24_7 === false && (
                              <Input className="flex-1" placeholder="Mensaje fuera de horario..." value={so.mensaje_fuera_horario || ""} onChange={(e) => setSubOpcion(idx, sidx, { mensaje_fuera_horario: e.target.value })} data-testid={`ivr-sub-fuera-horario-${idx}-${sidx}`} />
                            )}
                          </div>
                          )}
                          {so.tipo !== "faq" && so.tipo !== "submenu" && (
                          <div className="pl-12 space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-slate-500">📝 Preguntas de recolección (opcional)</span>
                              <Button variant="ghost" size="sm" className="h-6 px-2 text-emerald-700" onClick={() => setSubOpcion(idx, sidx, { preguntas: [...(so.preguntas || []), ""] })} data-testid={`ivr-sub-agregar-pregunta-${idx}-${sidx}`}>
                                <Plus className="w-3 h-3 mr-1" />Pregunta
                              </Button>
                            </div>
                            {(so.preguntas || []).map((pq, pi) => (
                              <div key={pi} className="flex items-center gap-1">
                                <span className="text-xs text-slate-400 shrink-0 w-4">{pi + 1}.</span>
                                <Input className="h-8 text-sm" placeholder="Ej: ¿A nombre de quién está la póliza?" value={pq}
                                  onChange={(e) => setSubOpcion(idx, sidx, { preguntas: so.preguntas.map((x, j) => (j === pi ? e.target.value : x)) })}
                                  data-testid={`ivr-sub-pregunta-${idx}-${sidx}-${pi}`} />
                                <Button variant="ghost" size="sm" className="text-red-500 h-7 w-7 p-0 shrink-0" onClick={() => setSubOpcion(idx, sidx, { preguntas: so.preguntas.filter((_, j) => j !== pi) })} data-testid={`ivr-sub-quitar-pregunta-${idx}-${sidx}-${pi}`}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            ))}
                          </div>
                          )}
                        </div>
                      ))}
                      <Button variant="outline" size="sm" onClick={() => setOpcion(idx, { sub_opciones: [...(op.sub_opciones || []), { ...SUB_VACIA }] })} data-testid={`ivr-agregar-sub-${idx}`}>
                        <Plus className="w-4 h-4 mr-1" />Agregar sub-opción
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Reintentos antes de pasar a un agente</Label>
                <Input type="number" min={1} max={5} value={form.max_reintentos} onChange={(e) => setForm({ ...form, max_reintentos: e.target.value })} data-testid="ivr-max-reintentos" />
              </div>
              <div>
                <Label>Reactivar menú tras inactividad (horas)</Label>
                <Input type="number" min={0} value={form.reactivar_horas} onChange={(e) => setForm({ ...form, reactivar_horas: e.target.value })} data-testid="ivr-reactivar-horas" />
                <p className="text-xs text-slate-500 mt-1">0 = solo en conversaciones nuevas</p>
              </div>
              <div>
                <Label>Alertar al supervisor si no atienden (min)</Label>
                <Input type="number" min={0} value={form.alerta_sin_atencion_minutos ?? 0} onChange={(e) => setForm({ ...form, alerta_sin_atencion_minutos: e.target.value })} data-testid="ivr-alerta-sin-atencion" />
                <p className="text-xs text-slate-500 mt-1">0 = desactivada. Avisa por correo y notificación al supervisor del ejecutivo asignado</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label>Mensaje de opción inválida</Label>
                <Input value={form.mensaje_invalido} onChange={(e) => setForm({ ...form, mensaje_invalido: e.target.value })} data-testid="ivr-mensaje-invalido" />
              </div>
              <div>
                <Label>Mensaje de confirmación <span className="text-xs text-slate-400">(use {"{departamento}"})</span></Label>
                <Input value={form.mensaje_confirmacion} onChange={(e) => setForm({ ...form, mensaje_confirmacion: e.target.value })} data-testid="ivr-mensaje-confirmacion" />
              </div>
              <div>
                <Label>Mensaje al pasar a un agente (fallback)</Label>
                <Input value={form.mensaje_fallback} onChange={(e) => setForm({ ...form, mensaje_fallback: e.target.value })} data-testid="ivr-mensaje-fallback" />
              </div>
            </div>

            <div className="border rounded-lg p-3 bg-emerald-50/50 dark:bg-emerald-900/10">
              <Label className="flex items-center gap-2 mb-2"><Eye className="w-4 h-4" />Vista previa del menú</Label>
              <pre className="text-sm whitespace-pre-wrap font-sans text-slate-700 dark:text-slate-200" data-testid="ivr-vista-previa">{buildPreview(form)}</pre>
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="ivr-form-activo" />
              <Label>IVR activo</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
            <Button onClick={handleGuardar} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ivr-guardar-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Guardar menú IVR
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WhatsAppIVRSection;
